import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { moveIndex, typeaheadIndex } from './overlayCore';
import { groupStep, verticalStep } from './menuRadioModel';

/**
 * WAI-ARIA menu (T-28, PRD §4.7): roving focus, ↑/↓ (wrap), Home/End,
 * first-letter typeahead, Escape, Tab out. Rows are 32 px (44 px on touch),
 * `rounded-lg`, monochrome icons. Lives inside `ui/Popover` (desktop) or a
 * sheet (`ui/Dialog variant="sheet"`, mobile).
 *
 * Selecting an item first dismisses the menu (the owner returns focus to the
 * trigger), then runs the item on the next frame, so a dialog the item opens
 * remembers the trigger as its opener.
 */
export interface MenuItemDef {
  key: string;
  label: ReactNode;
  /** Plain text for typeahead when `label` is not a string. */
  textValue?: string;
  icon?: ReactNode;
  /** Second line (what the action will use). */
  hint?: ReactNode;
  /** End-side content (state, shortcut). */
  trailing?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Draw a hairline above this item. */
  separatorBefore?: boolean;
  /** `lang` of the label (language endonyms). */
  lang?: string;
  /** Accessible name when the visible label is not enough. */
  ariaLabel?: string;
  /** `data-*` hooks (test selectors) kept on the item. */
  data?: Record<string, string>;
  /**
   * A choice in an inline radio row (`menuitemradio`, e.g. the appearance row, T-53).
   * Consecutive items with the same group id share one row, labelled by `label`;
   * ↑/↓ stop once on the row (its checked choice), ←/→ move between the choices.
   */
  radioGroup?: { id: string; label: string; icon?: ReactNode };
  /** `aria-checked` of a radio choice. */
  checked?: boolean;
  /** Selecting keeps the menu open (a radio choice that applies live). */
  keepOpen?: boolean;
}

export type MenuDismissReason = 'escape' | 'tab' | 'select';

interface MenuProps {
  items: MenuItemDef[];
  onDismiss: (reason: MenuDismissReason) => void;
  id?: string;
  label?: string;
  labelledBy?: string;
  /** Touch-sized rows and a larger icon tile (sheets). */
  large?: boolean;
  /** Focus the first item when mounted (default true). */
  autoFocus?: boolean;
  /** Tab leaves the menu (popover). In a sheet, the dialog traps Tab instead. */
  closeOnTab?: boolean;
}

export default function Menu({ items, onDismiss, id, label, labelledBy, large = false, autoFocus = true, closeOnTab = true }: MenuProps) {
  const disabled = items.map((i) => !!i.disabled);
  const [active, setActive] = useState(() => moveIndex('first', -1, disabled));
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const baseId = useId();

  useEffect(() => {
    if (!autoFocus) return;
    const first = moveIndex('first', -1, items.map((i) => !!i.disabled));
    // Deferred so it wins over a parent dialog that focuses its panel on open.
    const raf = requestAnimationFrame(() => { if (first >= 0) refs.current[first]?.focus({ preventScroll: true }); });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const focusAt = (i: number) => {
    if (i < 0) return;
    setActive(i);
    refs.current[i]?.focus();
  };

  const select = (item: MenuItemDef) => {
    if (item.disabled) return;
    if (item.keepOpen) { item.onSelect?.(); return; }
    onDismiss('select');
    if (item.onSelect) requestAnimationFrame(() => item.onSelect?.());
  };

  const groups = items.map((i) => i.radioGroup?.id);
  const checked = items.map((i) => !!i.checked);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusAt(verticalStep('next', active, disabled, groups, checked)); return;
      case 'ArrowUp': e.preventDefault(); focusAt(verticalStep('prev', active, disabled, groups, checked)); return;
      case 'ArrowLeft':
      case 'ArrowRight': {
        if (!groups[active]) return;
        e.preventDefault();
        const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
        focusAt(groupStep((e.key === 'ArrowLeft') === rtl, active, disabled, groups));
        return;
      }
      case 'Home': e.preventDefault(); focusAt(moveIndex('first', active, disabled)); return;
      case 'End': e.preventDefault(); focusAt(moveIndex('last', active, disabled)); return;
      case 'Escape': e.preventDefault(); e.stopPropagation(); onDismiss('escape'); return;
      case 'Tab': if (closeOnTab) { e.preventDefault(); onDismiss('tab'); } return;
      default: {
        if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey || e.key === ' ') return;
        const labels = items.map((i) => i.textValue ?? (typeof i.label === 'string' ? i.label : ''));
        const next = typeaheadIndex(labels, active, e.key, disabled);
        if (next >= 0) { e.preventDefault(); focusAt(next); }
      }
    }
  };

  return (
    <div
      role="menu"
      id={id}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-orientation="vertical"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex flex-col gap-0.5 focus:outline-none"
    >
      {items.map((item, i) => item.radioGroup ? (
        groups[i - 1] === item.radioGroup.id ? null : (
          <Fragment key={item.key}>
            {item.separatorBefore && i > 0 && <div role="separator" className="mx-2 my-1 h-px bg-border" />}
            {/* Inline radio row: label at the start, the choices as a segmented control. */}
            <div
              role="group"
              aria-labelledby={`${baseId}-${item.radioGroup.id}`}
              className={`flex items-center gap-3 rounded-lg px-2.5 text-sm text-text ${large ? 'min-h-touch py-1.5' : 'min-h-control-sm py-1'}`}
            >
              {item.radioGroup.icon && (
                <span
                  className={`flex flex-shrink-0 items-center justify-center text-text-2 ${large ? 'size-8 rounded-lg bg-surface-subtle' : 'size-5'}`}
                  aria-hidden="true"
                >
                  {item.radioGroup.icon}
                </span>
              )}
              <span id={`${baseId}-${item.radioGroup.id}`} className="min-w-0 flex-1 truncate">{item.radioGroup.label}</span>
              <span className="flex flex-shrink-0 items-center gap-0.5 rounded-lg bg-surface-subtle p-0.5">
                {items.map((choice, k) => choice.radioGroup?.id !== item.radioGroup?.id ? null : (
                  <button
                    key={choice.key}
                    ref={(el) => { refs.current[k] = el; }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={!!choice.checked}
                    tabIndex={k === active ? 0 : -1}
                    aria-disabled={choice.disabled || undefined}
                    aria-label={choice.ariaLabel}
                    onClick={() => select(choice)}
                    onFocus={() => setActive(k)}
                    className={`rounded-md px-2.5 text-xs font-medium transition-colors duration-micro ${large ? 'min-h-touch' : 'min-h-7'} ${choice.checked ? 'bg-surface text-text shadow-xs' : 'text-text-2 hover:text-text'}`}
                    {...(choice.data ?? {})}
                  >
                    {choice.label}
                  </button>
                ))}
              </span>
            </div>
          </Fragment>
        )
      ) : (
        <Fragment key={item.key}>
          {item.separatorBefore && i > 0 && <div role="separator" className="mx-2 my-1 h-px bg-border" />}
          <button
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="menuitem"
            tabIndex={i === active ? 0 : -1}
            aria-disabled={item.disabled || undefined}
            aria-label={item.ariaLabel}
            lang={item.lang}
            onClick={() => select(item)}
            onFocus={() => setActive(i)}
            className={`menu-row group ${item.danger ? 'menu-row-danger' : ''} ${large ? 'min-h-touch py-2' : ''}`}
            {...(item.data ?? {})}
          >
            {item.icon && (
              <span
                className={`flex flex-shrink-0 items-center justify-center ${large ? 'size-8 rounded-lg bg-surface-subtle' : 'size-5'} ${item.danger ? 'text-danger-700' : 'text-text-2'}`}
                aria-hidden="true"
              >
                {item.icon}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate">{item.label}</span>
              {item.hint && <span className="block truncate text-xs text-text-2">{item.hint}</span>}
            </span>
            {item.trailing && <span className="flex flex-shrink-0 items-center gap-1.5 text-xs text-text-2">{item.trailing}</span>}
          </button>
        </Fragment>
      ))}
    </div>
  );
}
